/**
 * StatusBadge — CRITICAL: always shows BOTH a colored dot AND a text label.
 * Never uses color alone to convey status (a11y + brand hard rule).
 */
import type * as React from 'react';
import { cn } from '../lib/cn.ts';

export type StatusVariant =
  | 'online'
  | 'offline'
  | 'active'
  | 'inactive'
  | 'pending'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral';

const dotColors: Record<StatusVariant, string> = {
  online: 'bg-[var(--color-success)]',
  offline: 'bg-[var(--color-stone-mid)]',
  active: 'bg-[var(--color-success)]',
  inactive: 'bg-[var(--color-stone-mid)]',
  pending: 'bg-[var(--color-warm-stone)]',
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warm-stone-deep)]',
  error: 'bg-[var(--color-alert)]',
  neutral: 'bg-[var(--color-stone-mid)]',
};

const textColors: Record<StatusVariant, string> = {
  online: 'text-[var(--color-success)]',
  offline: 'text-[var(--color-stone-mid)]',
  active: 'text-[var(--color-success)]',
  inactive: 'text-[var(--color-stone-mid)]',
  pending: 'text-[var(--color-warm-stone-deep)]',
  success: 'text-[var(--color-success)]',
  warning: 'text-[var(--color-warm-stone-deep)]',
  error: 'text-[var(--color-alert)]',
  neutral: 'text-[var(--color-stone-mid)]',
};

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** The semantic status variant (controls dot + text color). */
  status: StatusVariant;
  /** The human-readable label — REQUIRED; never rely on color alone. */
  label: string;
  'data-testid'?: string;
}

function StatusBadge({
  status,
  label,
  className,
  'data-testid': testId,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1.5',
        'font-[var(--font-mono)] text-xs font-medium tracking-wide',
        textColors[status],
        className,
      )}
      {...props}
    >
      {/* Colored dot — decorative; real meaning is in the text label */}
      <span
        aria-hidden="true"
        className={cn('h-1.5 w-1.5 rounded-full shrink-0', dotColors[status])}
      />
      {/* Text label — the primary status indicator */}
      <span>{label}</span>
    </span>
  );
}

export { StatusBadge };
